// Shared sentiment-score -> visual mapping for Conversations V3.
// Kept isolated from ConversationsV2 — nothing here is imported by or from
// the V2 tree.

import faceOne from "../../../../assets/images/one.png";
import faceTwo from "../../../../assets/images/two.png";
import faceThree from "../../../../assets/images/three.png";
import faceFour from "../../../../assets/images/four.png";
import faceFive from "../../../../assets/images/five.png";

// Item-level scores are always an integer on -3..3 (see backend
// sentimentScoring.js). Bucket/overall scores are corroboration-weighted
// averages, so they're decimals — colored on a coarser 3-band scale instead.

const ITEM_COLORS = {
  strongNegative: { bg: "#fdecea", fg: "#c0362c" },
  mildNegative: { bg: "#fdf3e7", fg: "#b5650a" },
  neutral: { bg: "#f2f4f7", fg: "#667085" },
  mildPositive: { bg: "#eaf6ec", fg: "#2e7d32" },
  strongPositive: { bg: "#eaf6ec", fg: "#1b5e20" },
  unscored: { bg: "#f2f4f7", fg: "#98a2b3" },
};

/** Item-level (integer -3..3) score -> {bg, fg} */
export function getItemScoreColors(score) {
  if (score === null || score === undefined || Number.isNaN(score)) return ITEM_COLORS.unscored;
  if (score <= -2) return ITEM_COLORS.strongNegative;
  if (score === -1) return ITEM_COLORS.mildNegative;
  if (score === 0) return ITEM_COLORS.neutral;
  if (score === 1) return ITEM_COLORS.mildPositive;
  return ITEM_COLORS.strongPositive;
}

/** Bucket/overall (decimal) score -> {bg, fg} — coarser 3-band thresholds. */
export function getBucketScoreColors(score) {
  if (score === null || score === undefined || Number.isNaN(score)) return ITEM_COLORS.unscored;
  const abs = Math.abs(score);
  if (abs < 0.5) return ITEM_COLORS.neutral;
  if (abs < 1.5) return score < 0 ? ITEM_COLORS.mildNegative : ITEM_COLORS.mildPositive;
  return score < 0 ? ITEM_COLORS.strongNegative : ITEM_COLORS.strongPositive;
}

/** Maps a -3..3 score onto a 0-100% position for the diverging scale bar. */
export function scoreToBarPosition(score) {
  const clamped = Math.max(-3, Math.min(3, score ?? 0));
  return ((clamped + 3) / 6) * 100;
}

// Mouth paths per clamped integer score, in a 24x24 viewBox — reused from the
// approved design mockup (frown at -3 through smile at +3).
const MOUTH_PATHS = {
  "-3": "M8 17 Q12 12 16 17",
  "-2": "M8 16 Q12 13 16 16",
  "-1": "M8 15.5 Q12 13.5 16 15.5",
  "0": "M8 15 L16 15",
  "1": "M8 14.5 Q12 16.5 16 14.5",
  "2": "M8 14 Q12 18 16 14",
  "3": "M8 13.5 Q12 19.5 16 13.5",
};

/** Item score -> SVG mouth path for the face icon. Clamps/rounds to -3..3. */
export function getFaceMouthPath(score) {
  if (score === null || score === undefined || Number.isNaN(score)) return MOUTH_PATHS["0"];
  const clamped = Math.max(-3, Math.min(3, Math.round(score)));
  return MOUTH_PATHS[String(clamped)];
}

export const DIVERGING_GRADIENT =
  "linear-gradient(90deg, #b71c1c 0%, #e57373 25%, #cfd4da 50%, #81c784 75%, #1b5e20 100%)";

// Only 5 face images exist (one.png..five.png = a 1-5 frown-to-smile scale),
// but item scores are a 7-point range (-3..+3). There's no visually distinct
// asset for "very negative" vs "extremely negative" (same for the positive
// end), so the two extra levels reuse the boundary image on each side:
// -3 and -2 both use one.png, +2 and +3 both use five.png.
const FACE_IMAGES = {
  "-3": faceOne,
  "-2": faceOne,
  "-1": faceTwo,
  "0": faceThree,
  "1": faceFour,
  "2": faceFive,
  "3": faceFive,
};

/** Item score -> face image src. Clamps/rounds to -3..3; null/undefined -> neutral (three.png). */
export function getFaceImage(score) {
  if (score === null || score === undefined || Number.isNaN(score)) return faceThree;
  const clamped = Math.max(-3, Math.min(3, Math.round(score)));
  return FACE_IMAGES[String(clamped)];
}
